import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Channel, ChannelService, User, VoipExtension } from 'lib-trend-core';
import { takeUntil } from 'rxjs';

export interface RamalFormDialogData {
  channel: Channel;
  users: Array<User>;
  ramal?: VoipExtension;
}

@Component({
    selector: 'channel-voip-ramal-form-component',
    templateUrl: './channel-voip-ramal-form.component.html',
    styleUrls: ['./channel-voip-ramal-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelVoipRamalFormComponent extends AbstractComponent implements OnInit {

  users: Array<User> = [];
  readonly ramalMaxLength = 4;

  constructor(
    injector: Injector,
    private readonly channelService: ChannelService,
    public dialogRef: MatDialogRef<ChannelVoipRamalFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RamalFormDialogData,
  ) {
    super(injector);
    this.setupForm();
  }

  ngOnInit(): void {
    this.users = this.data?.users ?? [];
    this.isNew = !this.data?.ramal;
    if (!this.isNew) {
      this.populateForm(this.data.ramal);
    }
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      user: [null, [Validators.required]],
      sipUsername: [null, [Validators.required, Validators.pattern(/^[0-9]+$/), Validators.maxLength(this.ramalMaxLength)]],
      activated: [true],
    });
  }

  private populateForm(ramal: VoipExtension): void {
    this.formGroup.patchValue({
      user: ramal.user?._id,
      sipUsername: ramal.sipUsername,
      activated: ramal.activated,
    });
  }

  save(): void {
    if (!this.formGroup.valid) {
      this.validateAllFormFields(this.formGroup);
      return;
    }
    this.loading = true;
    const ramal: VoipExtension = this.formGroup.value;
    const request$ = this.isNew
      ? this.channelService.addExtension(this.data.channel._id, ramal)
      : this.channelService.updateExtension(this.data.channel._id, this.data.ramal.sipUsername ?? null, ramal);

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (result: VoipExtension) => {
        this.alertService.success(this.t(this.isNew ? 'channel.voipRamalForm.ramalCreatedSuccess' : 'channel.voipRamalForm.ramalUpdatedSuccess'));
        this.loading = false;
        this.dialogRef.close(result ?? true);
      },
      error: (err) => {
        this.loading = false;
        this.alertService.error(err?.error?.message);
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
